import { css } from '@emotion/react';
import { useState, useRef, useEffect } from 'react';
import { Form, Row, Text, colors, Col, Icon, TextInput } from 'common-components';
import { useForm, Controller } from 'react-hook-form';
import { useAppTranslation } from '@hooks';
import { TxKeyPath } from '@i18n';
import { SelectRegular, RegularSelectPropsType } from '@components/inputs';
import { VerticalFormItem } from '@components/display';
import { SkillType, TSkillType } from '@definitions/states';
import { UpdateSegmentSkillPayloadType } from '@api/segment';
import { SegmentType } from '@pages/types';
import { AssignManager } from '@views/shared/manager';

import { HeaderSegment, StepHeading } from '../HeaderSegment';

import { store } from '@/store';

const type = 20;

type CompetencePropsTyp = {
  step: number
  segment: SegmentType
  onUpdateManagers: (managersSegment: SegmentType['managersShort']) => void
};

const CompetenceSegment = ({ step, segment, onUpdateManagers }: CompetencePropsTyp) => {
  const updateSkillSegmentAction = store.use.updateSkillSegmentAction().call;

  const { translate } = useAppTranslation();

  const [selectValue, setSelectValue] = useState<string>('');
  const [valuesSelected, setValuesSelected] = useState<RegularSelectPropsType['options']>([]);

  const formDataRef = useRef<UpdateSegmentSkillPayloadType>(null);
  const { watch, control } =
    useForm<Pick<UpdateSegmentSkillPayloadType, 'percentageForValidation' | 'qualityMin'>>();

  const submitData = watch();

  useEffect(() => {
    formDataRef.current = {
      ...submitData,
      idSegment: segment.idSegment,
      skills: valuesSelected.map((elem) => Number(elem.value) as TSkillType),
    };
  }, [submitData]);

  useEffect(() => {
    if (step === 3) {
      if (!formDataRef.current.idSegment || formDataRef.current.skills.length === 0) {
        return;
      }
      updateSkillSegmentAction(formDataRef.current);
    }
  }, [step]);

  if (step === 3) {
    return (
      <>
        <HeaderSegment type={type} name={segment.name} />
        <Row justify={'space-between'} align={'middle'}>
          <Col
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <StepHeading step={3} title={translate('createSegment.chooseManagers')} />
          </Col>
        </Row>
        <AssignManager
          managersUser={segment.managersShort}
          onUpdateManagers={onUpdateManagers}
          modifEnabled={true}
        />
      </>
    );
  }

  return (
    <div>
      <HeaderSegment type={type} name={segment.name} />
      <Row
        css={css`
          flex-direction: column;
        `}
      >
        <StepHeading step={step} title={translate('createSegment.skillCriterion')} />
      </Row>
      <Form
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 16px;
        `}
      >
        <SelectRegular
          options={Object.keys(SkillType)
            .map((value) => ({
              value,
              label: translate(`common.states.skill.${value}` as TxKeyPath),
            }))
            .filter((elem) => !valuesSelected.some((el) => el.value === elem.value))}
          setValue={setSelectValue}
          value={selectValue}
          valuesSelected={valuesSelected}
          setValuesSelected={setValuesSelected}
          placeholder={translate('createSegment.searchSkill')}
        />
        <Text color={colors.gray600}>{translate('createSegment.usersAreEvaluated')}</Text>
        {!!valuesSelected.length && (
          <div>
            {valuesSelected.map((elem) => {
              return (
                <Row
                  justify={'space-between'}
                  align="middle"
                  css={css`
                    padding: 8px 0;
                  `}
                  key={elem.value}
                >
                  <Text color={colors.gray700} style={{ fontWeight: 500 }}>
                    {translate(`common.states.skill.${elem.value}` as TxKeyPath)}
                  </Text>
                  <div
                    css={css`
                      display: flex;
                      padding: 8px;
                      cursor: pointer;
                    `}
                    onClick={() => {
                      const newArr = valuesSelected.filter((el) => el.value !== elem.value);
                      setValuesSelected(newArr);
                    }}
                  >
                    <Icon width={20} height={20} color={colors.gray500} icon="XClose" />
                  </div>
                </Row>
              );
            })}
          </div>
        )}
        <div
          css={css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          `}
        >
          <Controller
            name={'percentageForValidation'}
            control={control}
            render={({ field }) => {
              return (
                <VerticalFormItem label={translate('createSegment.minimumNumberOfTreatmentsEvaluated')}>
                  <TextInput
                    placeholder="35"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                    }
                  />
                </VerticalFormItem>
              );
            }}
          />

          <Controller
            name={'qualityMin'}
            control={control}
            render={({ field }) => {
              return (
                <VerticalFormItem label={translate('createSegment.minimumQuality')}>
                  <TextInput
                    placeholder="80%"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                    }
                  />
                </VerticalFormItem>
              );
            }}
          />
        </div>
      </Form>
      {/* HIDE
      <AudienceStat />
      */}
    </div>
  );
};

export default CompetenceSegment;
